<template>
  <!-- 全局等比缩放（8/31 拍板，参考绝区零官网）：宽度基准 1440、放大无上限、
       缩小 0.75 兜底，所有页面统一吃同一个 zoom，切页不跳大小；
       链接页（Home）是整屏固定构图，走自己的适配逻辑，不吃全局 zoom -->
  <div class="app-zoom" :style="appZoomStyle">
    <router-view />
    <!-- 串口被其它标签页 / 桌面 App 接管时的全局提示（点击本窗口任意处即触发夺回） -->
    <transition name="preempt-fade">
      <div v-if="preempted" class="preempt-banner" :class="{ 'preempt-banner--no-sidebar': route.name === 'Home' }">
        {{ t('serialBridge.preempted') }}
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/store/device'
import { usePageZoom } from '@/composables/usePageZoom'
import { prefetchThemeSkins } from '@/theme/theme-skin-data'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const { isConnected, preempted, valorantEdition, flashUiVersion } = storeToRefs(useDeviceStore())

// 识别到联名版即预取皮肤清单 + 预热原画：用户从仪表盘切到设置页时主题模块立即就绪
// 附带设备 flash UI 版本，清单按设备当前皮肤排序，与 Control 页组件同源同缓存
watch(
  [valorantEdition, locale],
  ([isValorant]) => {
    if (isValorant) prefetchThemeSkins(String(locale.value), () => flashUiVersion.value)
  },
  { immediate: true },
)

const { rootStyle: pageZoomRootStyle } = usePageZoom(1440, 0.75)
const appZoomStyle = computed(() =>
  route.name === 'Home' ? undefined : pageZoomRootStyle.value,
)

/* 全局导航（与桌面 App 一致的插拔体验）：
 * 连接建立（手动选择 / 插入自动连 / 多标签页·App 间接管）→ 从首页自动进设备页；
 * 连接断开（拔出 / 手动断开）→ 设备页回首页；Support 等页不打断；
 * 被其它标签页/App 抢占时留在原页，等焦点回来夺回。 */
const DEVICE_ROUTES = new Set(['Dashboard', 'Control'])
// preempted 也要观察：被抢占的 tab 停在设备页，若此时设备被拔出，
// isConnected 已是 false（无变化），仅 preempted 清零这一信号能把它带回首页
watch([isConnected, preempted], async ([connected, p]) => {
  await router.isReady()
  const name = router.currentRoute.value.name as string | undefined
  if (connected) {
    // 只从首页自动跳：用户正在看 FAQ / 填反馈时设备连上不强行打断
    if (!name || name === 'Home') router.push({ name: 'Dashboard' })
  } else if (!p && name && DEVICE_ROUTES.has(name)) {
    // replace 而非 push：把已断连的设备页从历史栈移除，避免 Back 回到断连页
    router.replace({ name: 'Home' })
  }
})
</script>

<style>
#app {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
}

/* 缩放包裹层兜底底色：zoom 低于 1（下限区间）时页面自身 100vh 不满视口 */
.app-zoom {
  background: #181a1b;
}

.preempt-banner {
  position: fixed;
  top: 10%;
  /* 在侧边栏（200px）右侧的内容区内居中：中心右移半个侧边栏宽度 */
  left: calc(50% + 100px);
  transform: translate(-50%, -50%);
  max-width: calc(100vw - 32px);
  padding: 10px 24px;
  border-radius: 22px;
  background: rgba(20, 20, 20, 0.92);
  border: 1px solid #555;
  color: #ffffff;
  font-size: 14px;
  line-height: 1.5;
  white-space: nowrap;
  z-index: 2000;
  backdrop-filter: blur(6px);
  pointer-events: none;
}

/* 无侧边栏的页面（设备连接页 Home 为整屏居中构图）：按视口居中，不吃侧边栏偏移 */
.preempt-banner--no-sidebar {
  left: 50%;
}

.preempt-fade-enter-active,
.preempt-fade-leave-active {
  transition: opacity 0.25s ease;
}
.preempt-fade-enter-from,
.preempt-fade-leave-to {
  opacity: 0;
}
</style>
